import React from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import {
  BulkImportUserInput,
  InvestmentCategoryName,
  InvestmentGeographyName,
  InvestmentStageName
} from 'generated/graphql';
import _ from 'lodash';
import {
  appEvents,
  CSVField,
  validationUtil
} from 'modules';
import * as yup from 'yup';

import * as dataService from './dataService';



interface ErrorMessage {
  code: 'EMAILS_IN_USE' | 'DUPLICATE_EMAILS';
  data: string[];
}



export interface IProps {
  type: 'investors' | 'startups';
}

export const FC: React.FC<IProps> = ({ type }) => {
  const tuple = dataService.useQuery();
  const [hasError, setHasError] = React.useState(false);
  const [rows, setRows] = React.useState<ICSVRow[]>([]);
  const [errMsg, setErrMsg] = React.useState<ErrorMessage | null>(null);



  function handleClose() {
    appEvents.broadcast({
      type: 'modal',
      args: 'close'
    });
  }



  async function handleClickConfirm() {
    const emails = _.compact(_.map(rows, row => row.email));
    const duplicateEmails = _.filter(emails, (m, index) => emails.lastIndexOf(m) !== index);
    if (!_.isEmpty(duplicateEmails)) {
      setErrMsg({
        code: 'DUPLICATE_EMAILS',
        data: duplicateEmails
      });
      return;
    }
    const invalidEmails = await tuple.queries.validateEmailsAvailable(emails);
    if (!_.isEmpty(invalidEmails)) {
      setErrMsg({
        code: 'EMAILS_IN_USE',
        data: invalidEmails
      });
      return;
    }
    setErrMsg(null);

    await tuple.mutations.bulkImportUsers({
      userType: type === 'investors' ? 'investor' : 'startupFounder',
      users: _.map(rows, (row): BulkImportUserInput => ({
        email: row.email ?? undefined,
        firstName: row.firstName,
        lastName: row.lastName,
        companyName: row.companyName,
        checkSizeMin: type === 'investors' ? (row.checkSizeMin || undefined) : undefined,
        checkSizeMax: type === 'investors' ? (row.checkSizeMax || undefined) : undefined,
        city: row.city,
        country: row.country,
        companyDescription: row.companyDescription,
        phoneNumber: row.phoneNumber || undefined,
        logoUrl: row.logoUrl,
        websiteUrl: row.websiteUrl,
        usState: row.usState || undefined,
        postalCode: row.postalCode || undefined,
        investmentCategories: row.investmentCategories as InvestmentCategoryName[],
        investmentStages: row.investmentStages as InvestmentStageName[],
        investmentAreas: _.compact(_.map(row.investmentAreas, a => tuple.data.investmentAreaDisplayName2Id[a ?? '']?.id ?? undefined)),
        investmentGeographies: row.investmentGeographies as InvestmentGeographyName[]
      }))
    });
    handleClose();
  }



  return (
    <Dialog
      open
      onClose={() => !tuple.loading && handleClose()}
      maxWidth={hasError ? 'xl' : undefined}>
      <DialogTitle>
        Add {_.upperFirst(type)}
      </DialogTitle>

      <DialogContent style={{ width: hasError? '1024px' : undefined }}>
        <Stack spacing={1}>
          <Typography variant="body2">
            Select a CSV file from your device containing a list of {type} to add to our system.
          </Typography>

          <CSVField.FC
            disabled={tuple.loading}
            rowValidator={__csvSchema}
            key2FnTransformRow={{
              investmentAreas: __stringVal2Array,
              investmentCategories: __stringVal2Array,
              investmentStages: __stringVal2Array,
              investmentGeographies: __stringVal2Array
            }}
            onSuccess={results => {
              setRows(results);
              setHasError(false);
            }}
            onError={() => {
              setRows([]);
              setHasError(true);
            }}
            onDelete={() => setRows([])} />

          {!_.isEmpty(rows) && !errMsg && (
            <Typography>CSV has {rows.length} rows to import.</Typography>
          )}

          {errMsg !== null && __renderErrorMessage(errMsg)}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="secondary"
          disabled={tuple.loading}
          onClick={handleClose}>
          Cancel
        </Button>

        <Button
          disabled={tuple.loading || hasError || _.isEmpty(rows)}
          onClick={handleClickConfirm}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};



type ICSVRow = yup.InferType<typeof __csvSchema>;



const __csvSchema = yup.object({
  firstName: yup.string(),
  lastName: yup.string(),
  email: validationUtil.emailSchema,
  companyName: yup.string().required('Company name is required.'),
  phoneNumber: yup.string(),
  websiteUrl: validationUtil.websiteUrlSchema.required('Website URL is required.'),
  logoUrl: yup.string()
    .url()
    .required('Logo URL is required.')
    .transform((value: string) => {
      const replacementRegex = /\/https?:\/\//gm;

      // This is a case to handle valid clearbit urls specifically.
      if (_.includes(value, 'logo.clearbit.com') && replacementRegex.test(value)) {
        const transformedValue = _.replace(value, replacementRegex, '/http:/');
        return transformedValue;
      }

      return value;
    }).test(
      'Logo URL',
      'Does not have a valid URL to an image, or the image is not found.',
      async value => {
        if (value) {
          try {
            const headers = new Headers();
            const response = await fetch(value, {
              method: 'GET',
              redirect: 'follow',
              headers
            });
            const mimetype = response.headers.get('Content-Type');
            if (!mimetype || !_.includes(mimetype, 'image')) {
              return false;
            }
          } catch (e) {
            return false;
          }
          return true;
        }

        return true;
      }),
  investmentCategories: yup.array().of(validationUtil.investmentCategorySchema).required('Investment category is required.'),
  investmentAreas: yup.array().of(yup.string()).required('Investment area is required.'),
  investmentStages: yup.array().of(validationUtil.investmentStageSchema).required('Investment stage is required.'),
  investmentGeographies: yup.array().of(validationUtil.investmentGeographySchema).required('Investment geography is required.'),
  companyDescription: yup.string().required('Company description is required.'),
  checkSizeMin: yup.string().matches(/^((-?[0-9]+\.[0-9]*)|(-?[0-9]*\.[0-9]+)|(-?[0-9]+))$/, 'Check size minimum must be a number without any special characters.'),
  checkSizeMax: yup.string().matches(/^((-?[0-9]+\.[0-9]*)|(-?[0-9]*\.[0-9]+)|(-?[0-9]+))$/, 'Check size minimum must be a number without any special characters.'),
  country: yup.string().required('Country is required.'),
  city: yup.string().required('City is required.'),
  usState: validationUtil.usStateSchema,
  postalCode: yup.string()
}).required();



function __renderErrorMessage(errMsg: ErrorMessage) {
  if (errMsg.code === 'EMAILS_IN_USE') {
    return (
      <>
        <Typography>Some emails were already in use:</Typography>

        {_.map(errMsg.data, (d, i) => (
          <Typography
            key={i}
            variant="body2">
            {d} is already in use.
          </Typography>
        ))}
      </>
    );
  } else {
    return (
      <>
        <Typography>Duplicate email accounts found:</Typography>

        {_.map(errMsg.data, (d, i) => (
          <Typography
            key={i}
            variant="body2">
            {d}
          </Typography>
        ))}
      </>
    );
  }
}



function __stringVal2Array(val?: string | Array<string | undefined>): undefined | string[] {
  if (_.isNil(val)) {
    return undefined;
  }

  if (_.isArray(val)) {
    return _.compact(val);
  }
  const stringList = _.compact(_.map(_.split(val, ';'), _.trim));
  return _.isEmpty(stringList) ? undefined : stringList;
}
